import { Injectable } from '@angular/core';

/**
 * Risultato della validazione di un file
 */
export interface ValidationResult {
  valid: boolean;
  error?: string;
  details?: {
    failedCheck?: 'size' | 'mimeType' | 'extension' | 'magicNumbers';
    detectedBytes?: number[];
    expectedBytes?: number[][];
  };
}


/**
 * 🔒 Servizio centralizzato per la validazione sicura dei file
 * 
 * IMPORTANTE: Questi controlli sono necessari per UX e protezione base,
 * ma NON sono sufficienti per la sicurezza.
 * Il backend DEVE rivalidare tutto indipendentemente.
 * 
 * Controlli implementati:
 * 1. Dimensione file (max 10MB)
 * 2. MIME type verificato dal browser
 * 3. Estensione file
 * 4. Magic Numbers (primi byte del file - file signature)
 * 
 * @see BACKEND_FILE_UPLOAD_SECURITY.md per requisiti backend
 */
@Injectable({
  providedIn: 'root'
})
export class FileValidationService {

  /**
   * Configurazioni di validazione per documenti (PDF, Word)
   */
  private readonly DOCUMENT_CONFIG = {
    maxSizeBytes: 10 * 1024 * 1024, // 10 MB
    allowedMimeTypes: [
      'application/pdf',
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' // .docx
    ],
    allowedExtensions: ['.pdf', '.doc', '.docx'],
    magicNumbers: {
      pdf: [0x25, 0x50, 0x44, 0x46], // %PDF
      doc: [0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1], // OLE Compound File
      docx: [0x50, 0x4B, 0x03, 0x04], // PKZip (DOCX è un ZIP)
      // Aggiunta verifica alternativa per DOCX (alcuni hanno [0x50, 0x4B, 0x05, 0x06] o [0x50, 0x4B, 0x07, 0x08])
      docxAlt1: [0x50, 0x4B, 0x05, 0x06],
      docxAlt2: [0x50, 0x4B, 0x07, 0x08]
    }
  };

  /**
   * Configurazioni di validazione per attachments (PDF, Word, Excel, PowerPoint)
   */
  private readonly ATTACHMENT_CONFIG = {
    maxSizeBytes: 10 * 1024 * 1024, // 10 MB
    allowedMimeTypes: [
      'application/pdf',
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-powerpoint', // .ppt
      'application/vnd.openxmlformats-officedocument.presentationml.presentation' // .pptx
    ],
    allowedExtensions: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'],
    magicNumbers: {
      pdf: [0x25, 0x50, 0x44, 0x46], // %PDF
      ole: [0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1], // OLE Compound File (DOC, XLS, PPT)
      zip: [0x50, 0x4B, 0x03, 0x04], // PKZip (DOCX, XLSX, PPTX)
      zipAlt1: [0x50, 0x4B, 0x05, 0x06],
      zipAlt2: [0x50, 0x4B, 0x07, 0x08]
    }
  };

  /**
   * Configurazioni di validazione per file checklist (PDF, Word, Excel, PowerPoint, Immagini)
   */
  private readonly CHECKLIST_CONFIG = {
    maxSizeBytes: 10 * 1024 * 1024, // 10 MB
    allowedMimeTypes: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'image/jpeg',
      'image/jpg',
      'image/png'
    ],
    allowedExtensions: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.jpg', '.jpeg', '.png'],
    magicNumbers: {
      pdf: [0x25, 0x50, 0x44, 0x46], // %PDF
      ole: [0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1], // OLE Compound File
      zip: [0x50, 0x4B, 0x03, 0x04], // PKZip
      zipAlt1: [0x50, 0x4B, 0x05, 0x06],
      zipAlt2: [0x50, 0x4B, 0x07, 0x08],
      jpeg: [0xFF, 0xD8, 0xFF], // JPEG
      png: [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A] // PNG
    }
  };

  /**
   * Configurazione per gli allegati "Controlli e Revisione": solo PDF, fino a 100 MB per file.
   */
  private readonly REVIEW_DOCUMENT_CONFIG = {
    maxSizeBytes: 100 * 1024 * 1024, // 100 MB
    allowedMimeTypes: ['application/pdf'],
    allowedExtensions: ['.pdf'],
    magicNumbers: {
      pdf: [0x25, 0x50, 0x44, 0x46] // %PDF
    }
  };

  /**
   * Valida un file per upload di documenti contrattuali (PDF, Word)
   * Esegue tutti i controlli in sequenza
   * 
   * @param file - Il file da validare
   * @returns Promise con risultato della validazione
   */
  async validateDocument(file: File): Promise<ValidationResult> {
    return this.validateFile(file, this.DOCUMENT_CONFIG);
  }

  /**
   * Valida un allegato dei "Controlli e Revisione": solo PDF, fino a 100 MB per file.
   *
   * @param file - Il file da validare
   * @returns Promise con risultato della validazione
   */
  async validateReviewDocument(file: File): Promise<ValidationResult> {
    return this.validateFile(file, this.REVIEW_DOCUMENT_CONFIG);
  }

  /**
   * Valida un file per upload di attachments (PDF, Word, Excel, PowerPoint)
   * Esegue tutti i controlli in sequenza
   * 
   * @param file - Il file da validare
   * @returns Promise con risultato della validazione
   */
  async validateAttachment(file: File): Promise<ValidationResult> {
    return this.validateFile(file, this.ATTACHMENT_CONFIG);
  }

  /**
   * Valida un file per upload di checklist (PDF, Word, Excel, PowerPoint, Immagini)
   * Esegue tutti i controlli in sequenza
   * Supporta: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, JPEG, PNG
   * 
   * @param file - Il file da validare
   * @returns Promise con risultato della validazione
   */
  async validateFileChecklist(file: File): Promise<ValidationResult> {
    return this.validateFile(file, this.CHECKLIST_CONFIG);
  }

  /**
   * Valida un file generico con la configurazione specificata
   * @private
   */
  private async validateFile(file: File, config: typeof this.DOCUMENT_CONFIG | typeof this.ATTACHMENT_CONFIG | typeof this.CHECKLIST_CONFIG | typeof this.REVIEW_DOCUMENT_CONFIG): Promise<ValidationResult> {
    // 1. Controllo dimensione
    const sizeCheck = this.validateFileSize(file, config);
    if (!sizeCheck.valid) {
      return sizeCheck;
    }

    // 2. Controllo MIME type
    const mimeCheck = this.validateMimeType(file, config);
    if (!mimeCheck.valid) {
      return mimeCheck;
    }

    // 3. Controllo estensione
    const extensionCheck = this.validateExtension(file, config);
    if (!extensionCheck.valid) {
      return extensionCheck;
    }

    // 4. Controllo magic numbers (async - legge primi byte del file)
    const magicNumbersCheck = await this.validateMagicNumbers(file, config);
    if (!magicNumbersCheck.valid) {
      return magicNumbersCheck;
    }

    return { valid: true };
  }

  /**
   * Valida la dimensione del file
   * 
   * @param file - Il file da validare
   * @param config - Configurazione da usare
   * @returns Risultato validazione
   */
  private validateFileSize(file: File, config: typeof this.DOCUMENT_CONFIG | typeof this.ATTACHMENT_CONFIG | typeof this.CHECKLIST_CONFIG | typeof this.REVIEW_DOCUMENT_CONFIG): ValidationResult {
    if (file.size > config.maxSizeBytes) {
      const maxSizeMB = config.maxSizeBytes / (1024 * 1024);
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      return {
        valid: false,
        error: `Il file "${file.name}" è troppo grande (${fileSizeMB} MB). Dimensione massima: ${maxSizeMB} MB.`,
        details: { failedCheck: 'size' }
      };
    }
    return { valid: true };
  }

  /**
   * Valida il MIME type dichiarato dal browser
   * 
   * NOTA: Il browser legge il contenuto del file per determinare il MIME type,
   * non si basa solo sull'estensione. Tuttavia, può essere modificato da un attaccante esperto.
   * 
   * @param file - Il file da validare
   * @param config - Configurazione da usare
   * @returns Risultato validazione
   */
  private validateMimeType(file: File, config: typeof this.DOCUMENT_CONFIG | typeof this.ATTACHMENT_CONFIG | typeof this.CHECKLIST_CONFIG | typeof this.REVIEW_DOCUMENT_CONFIG): ValidationResult {
    if (!config.allowedMimeTypes.includes(file.type)) {
      return {
        valid: false,
        error: `Tipo di file non supportato: "${file.type}". Sono accettati solo PDF, DOC e DOCX.`,
        details: { failedCheck: 'mimeType' }
      };
    }
    return { valid: true };
  }

  /**
   * Valida l'estensione del file
   * 
   * NOTA: Questo è il controllo più debole - facilmente bypassabile rinominando il file.
   * È utile per impedire upload accidentali di file sbagliati.
   * 
   * @param file - Il file da validare
   * @param config - Configurazione da usare
   * @returns Risultato validazione
   */
  private validateExtension(file: File, config: typeof this.DOCUMENT_CONFIG | typeof this.ATTACHMENT_CONFIG | typeof this.CHECKLIST_CONFIG | typeof this.REVIEW_DOCUMENT_CONFIG): ValidationResult {
    const fileName = file.name.toLowerCase();
    const hasValidExtension = config.allowedExtensions.some(ext => 
      fileName.endsWith(ext)
    );

    if (!hasValidExtension) {
      return {
        valid: false,
        error: `Estensione file non valida. Sono accettati solo: ${config.allowedExtensions.join(', ')}`,
        details: { failedCheck: 'extension' }
      };
    }
    return { valid: true };
  }

  /**
   * Valida i Magic Numbers (file signature) leggendo i primi byte del file
   * 
   * 🔒 CONTROLLO CRITICO DI SICUREZZA
   * 
   * I magic numbers sono i primi byte di un file che ne identificano univocamente il tipo.
   * Non possono essere modificati senza corrompere il file.
   * 
   * Esempio: Un file .sql rinominato in .pdf sarà bloccato perché:
   * - SQL file non inizia con %PDF (25 50 44 46 in hex)
   * - Anche se MIME type fosse modificato, i byte reali del file non corrispondono
   * 
   * @param file - Il file da validare
   * @param config - Configurazione da usare
   * @returns Promise con risultato validazione
   */
  private async validateMagicNumbers(file: File, config: typeof this.DOCUMENT_CONFIG | typeof this.ATTACHMENT_CONFIG | typeof this.CHECKLIST_CONFIG | typeof this.REVIEW_DOCUMENT_CONFIG): Promise<ValidationResult> {
    try {
      // Leggi i primi 8 byte del file (sufficienti per identificare la maggior parte dei formati)
      const bytes = await this.readFileBytes(file, 8);

      // Verifica se i byte corrispondono a uno dei formati accettati
      const magicNumbers = config.magicNumbers;
      
      // Controllo PDF
      if ('pdf' in magicNumbers && this.matchesSignature(bytes, magicNumbers.pdf)) {
        return { valid: true };
      }

      // Controllo OLE Compound File (DOC, XLS, PPT)
      if ('doc' in magicNumbers && this.matchesSignature(bytes, magicNumbers.doc)) {
        return { valid: true };
      }
      if ('ole' in magicNumbers && this.matchesSignature(bytes, magicNumbers.ole)) {
        return { valid: true };
      }

      // Controllo PKZip (DOCX, XLSX, PPTX) - verifica tutte le varianti
      if ('docx' in magicNumbers && this.matchesSignature(bytes, magicNumbers.docx)) {
        return { valid: true };
      }
      if ('zip' in magicNumbers && this.matchesSignature(bytes, magicNumbers.zip)) {
        return { valid: true };
      }
      if ('docxAlt1' in magicNumbers && this.matchesSignature(bytes, magicNumbers.docxAlt1)) {
        return { valid: true };
      }
      if ('zipAlt1' in magicNumbers && this.matchesSignature(bytes, magicNumbers.zipAlt1)) {
        return { valid: true };
      }
      if ('docxAlt2' in magicNumbers && this.matchesSignature(bytes, magicNumbers.docxAlt2)) {
        return { valid: true };
      }
      if ('zipAlt2' in magicNumbers && this.matchesSignature(bytes, magicNumbers.zipAlt2)) {
        return { valid: true };
      }

      // Controllo JPEG
      if ('jpeg' in magicNumbers && this.matchesSignature(bytes, magicNumbers.jpeg)) {
        return { valid: true };
      }

      // Controllo PNG
      if ('png' in magicNumbers && this.matchesSignature(bytes, magicNumbers.png)) {
        return { valid: true };
      }

      // Nessuna signature riconosciuta
      let expectedFormats = 'PDF, DOC o DOCX';
      if (config === this.ATTACHMENT_CONFIG) {
        expectedFormats = 'PDF, DOC, DOCX, XLS, XLSX, PPT o PPTX';
      } else if (config === this.CHECKLIST_CONFIG) {
        expectedFormats = 'PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, JPEG o PNG';
      } else if (config === this.REVIEW_DOCUMENT_CONFIG) {
        expectedFormats = 'PDF';
      }
      
      return {
        valid: false,
        error: `Il file "${file.name}" non è un documento valido. Il contenuto reale non corrisponde a ${expectedFormats}.`,
        details: {
          failedCheck: 'magicNumbers',
          detectedBytes: Array.from(bytes),
          expectedBytes: Object.values(magicNumbers)
        }
      };

    } catch (error) {
      console.error('Errore durante la lettura dei magic numbers:', error);
      return {
        valid: false,
        error: `Impossibile leggere il contenuto del file "${file.name}".`,
        details: { failedCheck: 'magicNumbers' }
      };
    }
  }

  /**
   * Legge i primi N byte di un file usando FileReader API
   * 
   * @param file - Il file da leggere
   * @param numBytes - Numero di byte da leggere
   * @returns Promise con Uint8Array dei byte letti
   */
  private readFileBytes(file: File, numBytes: number): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event: ProgressEvent<FileReader>) => {
        if (event.target?.result) {
          const arrayBuffer = event.target.result as ArrayBuffer;
          const bytes = new Uint8Array(arrayBuffer);
          resolve(bytes);
        } else {
          reject(new Error('Impossibile leggere il file'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Errore durante la lettura del file'));
      };

      // Leggi solo i primi N byte come ArrayBuffer
      const blob = file.slice(0, numBytes);
      reader.readAsArrayBuffer(blob);
    });
  }

  /**
   * Verifica se i byte del file corrispondono a una signature attesa
   * 
   * @param fileBytes - Byte letti dal file
   * @param signature - Signature attesa
   * @returns true se corrispondono
   */
  private matchesSignature(fileBytes: Uint8Array, signature: number[]): boolean {
    if (fileBytes.length < signature.length) {
      return false;
    }

    for (let i = 0; i < signature.length; i++) {
      if (fileBytes[i] !== signature[i]) {
        return false;
      }
    }

    return true;
  }

  /**
   * Formatta la dimensione del file in formato leggibile
   * 
   * @param bytes - Dimensione in byte
   * @returns Stringa formattata (es. "2.5 MB")
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Ottiene la configurazione corrente per i documenti
   * Utile per mostrare limiti nella UI
   */
  getDocumentConfig() {
    return {
      maxSizeMB: this.DOCUMENT_CONFIG.maxSizeBytes / (1024 * 1024),
      allowedExtensions: [...this.DOCUMENT_CONFIG.allowedExtensions],
      allowedMimeTypes: [...this.DOCUMENT_CONFIG.allowedMimeTypes]
    };
  }
}
